import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Copy, Check, AlertTriangle, Lock, MessageSquare, FileText, Users, ShieldCheck,
  Quote, Phone, Mail, Presentation, Info, Search, Globe2, Share2, Download,
  Copyright, Layers,
} from "lucide-react";
import { api, API } from "@/lib/api";
import {
  MARKETING_VERSION, REQUIRED_DISCLAIMER, MUST_INCLUDE, NEVER_SAY,
  SOCIAL_GENERAL, SHORT_QUOTES, COUNTRY_TEMPLATES,
  FLYER_TEXT, EMAIL_TEMPLATE, SLIDES_OUTLINE,
  AUDIENCE_TEMPLATES, PHONE_SCRIPT, HOW_TO_USE,
} from "@/data/marketingTemplates";

const FOLDERS = [
  { key: "social",     label: "Social & WhatsApp", icon: <MessageSquare size={16}/> },
  { key: "print",      label: "Print & Outreach",  icon: <FileText size={16}/> },
  { key: "audience",   label: "By Audience",       icon: <Users size={16}/> },
  { key: "compliance", label: "Rules & Compliance",icon: <ShieldCheck size={16}/> },
];

const LANGUAGES = [
  { key: "en", label: "English" },
  { key: "sn", label: "Shona" },
  { key: "nd", label: "isiNdebele" },
  { key: "zu", label: "isiZulu" },
  { key: "xh", label: "isiXhosa" },
  { key: "af", label: "Afrikaans" },
  { key: "tn", label: "Setswana" },
  { key: "st", label: "Sesotho" },
  { key: "pt", label: "Português" },
];

function hasDisclaimerInBody(body) {
  const b = (body || "").toLowerCase();
  return b.includes("not a bank") && b.includes("independent legal title");
}

// Compose the shareable text: OFFICIAL BODY + optional signature + shortlink + disclaimer.
function composeShare({ body, contact, agentCode }) {
  let text = body || "";
  if (agentCode) {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const shortlink = `${origin}/r/${agentCode}`;
    text = text.replace(/\[Link\]/g, shortlink);
  }
  const sig = [];
  if (contact.name)  sig.push(`Agent: ${contact.name}`);
  if (contact.phone) sig.push(contact.phone);
  if (contact.email) sig.push(contact.email);
  const signature = sig.length ? `\n\n${sig.join(" · ")}` : "";
  const tail = hasDisclaimerInBody(text) ? "" : `\n\n${REQUIRED_DISCLAIMER}`;
  return `${text}${signature}${tail}`;
}

// --- Single copy card ---
function CopyCard({ id, title, body, contact, agentCode, testid, matches, activeSearch }) {
  const [copied, setCopied] = useState(false);
  const missing = !hasDisclaimerInBody(body);

  const finalText = useMemo(
    () => composeShare({ body, contact, agentCode }),
    [body, contact, agentCode]
  );

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(finalText);
      setCopied(true);
      toast.success("Copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard blocked — long-press to copy manually.");
    }
  };

  const doShare = async () => {
    if (!navigator.share) { doCopy(); return; }
    try {
      await navigator.share({ text: finalText });
      toast.success("Shared.");
    } catch (err) {
      if (err && err.name !== "AbortError") toast.error("Share cancelled.");
    }
  };

  // Search visibility
  if (activeSearch && !matches) return null;

  return (
    <div className="mv-card p-5 md:p-6" data-testid={testid} data-template-id={id}>
      {title && (
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="font-display text-[17px] mv-green-text">{title}</div>
          <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>
            <Lock size={11}/> Locked wording
          </span>
        </div>
      )}
      <pre
        className="whitespace-pre-wrap font-sans text-[14px] leading-relaxed"
        style={{ color: "var(--mv-ink)" }}
        data-testid={`${testid}-body`}
      >{body}</pre>

      {missing && (
        <div className="mt-3 flex items-start gap-2 text-[12.5px] p-3 rounded-xl"
             style={{ background: "rgba(212,60,60,0.08)", color: "#8a2020" }}
             data-testid={`${testid}-warning`}>
          <AlertTriangle size={14} className="mt-0.5"/>
          <div>Required disclaimer missing. It will be auto-appended when you copy.</div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 justify-end">
        {typeof navigator !== "undefined" && navigator.share && (
          <button
            type="button"
            onClick={doShare}
            className="btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] border mv-hairline hover:bg-black/[0.04]"
            data-testid={`${testid}-share`}
          >
            <Share2 size={14}/> Share
          </button>
        )}
        <button
          type="button"
          onClick={doCopy}
          className="btn-green"
          data-testid={`${testid}-copy`}
        >
          {copied ? <Check size={15}/> : <Copy size={15}/>}
          {copied ? "Copied" : "Copy to clipboard"}
        </button>
      </div>
    </div>
  );
}

function StructuredCard({ id, header, headline, bullets, quote, tags, contact, agentCode, testid, matches, activeSearch }) {
  const body = useMemo(() => {
    const lines = [];
    if (header)   lines.push(header);
    if (headline) lines.push(headline);
    if (bullets && bullets.length) {
      lines.push("");
      bullets.forEach((b) => lines.push(`✅ ${b}`));
    }
    if (quote) { lines.push(""); lines.push(`"${quote}"`); }
    if (tags)  { lines.push(""); lines.push(tags); }
    lines.push("");
    lines.push(REQUIRED_DISCLAIMER);
    return lines.join("\n");
  }, [header, headline, bullets, quote, tags]);
  return <CopyCard id={id} title={header || headline} body={body} contact={contact} agentCode={agentCode} testid={testid} matches={matches} activeSearch={activeSearch}/>;
}

function ContactEditor({ contact, setContact }) {
  const CACHE_KEY = "mv_agent_contact";
  const set = (k) => (e) => {
    const next = { ...contact, [k]: e.target.value };
    setContact(next);
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch {}
  };
  return (
    <div className="mv-card p-5 md:p-6" data-testid="mh-contact-editor">
      <div className="flex items-center gap-2 mb-2">
        <Info size={15} className="mv-green-text"/>
        <div className="font-display text-[16px] mv-green-text">Your contact block (optional)</div>
      </div>
      <p className="text-[12.5px]" style={{ color: "var(--mv-muted)" }}>
        Added to the end of anything you copy. Official wording above never changes.
      </p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: "var(--mv-muted)" }}>Your name</span>
          <input value={contact.name} onChange={set("name")} className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]" data-testid="mh-contact-name"/>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: "var(--mv-muted)" }}>Phone / WhatsApp</span>
          <input value={contact.phone} onChange={set("phone")} className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]" data-testid="mh-contact-phone"/>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: "var(--mv-muted)" }}>Email</span>
          <input value={contact.email} onChange={set("email")} className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]" data-testid="mh-contact-email"/>
        </label>
      </div>
    </div>
  );
}

// Merge defaults with admin overrides (keyed by stable template id)
function useOverrides() {
  const [ovr, setOvr] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/marketing/templates");
        setOvr(data.overrides || {});
      } catch {}
    })();
  }, []);
  return ovr;
}

const bodyOf = (id, def, ovr) => (ovr && ovr[id]) || def;

export default function MarketingHub({ agent }) {
  const [folder, setFolder] = useState("social");
  const [search, setSearch] = useState("");
  const [lang, setLang] = useState("en");
  const [contact, setContact] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("mv_agent_contact") || "null");
      if (cached) return cached;
    } catch {}
    return {
      name:  agent?.full_name || "",
      phone: agent?.phone     || "",
      email: agent?.email     || "",
    };
  });

  const overrides = useOverrides();
  const activeSearch = search.trim().length > 0;
  const sq = search.trim().toLowerCase();
  const match = (txt) => !activeSearch || (txt || "").toLowerCase().includes(sq);

  const agentCode = agent?.agent_code;

  // --- Build the "cards" list for the active folder (used for copy-all + rendering) ---
  const activeCards = useMemo(() => {
    const cards = [];
    if (folder === "social") {
      SOCIAL_GENERAL.forEach((line, i) => cards.push({
        id: `social.general.${i}`, title: `WhatsApp caption ${i + 1}`,
        body: bodyOf(`social.general.${i}`, line, overrides),
        testid: `mh-social-general-${i}`,
      }));
      SHORT_QUOTES.forEach((q, i) => cards.push({
        id: `social.quote.${i}`, title: `Short quote ${i + 1}`,
        body: bodyOf(`social.quote.${i}`, q, overrides),
        testid: `mh-short-quote-${i}`,
      }));
      COUNTRY_TEMPLATES.forEach((c) => {
        const id = `social.country.${c.country.toLowerCase().replace(/\s+/g, "-")}`;
        cards.push({
          id, title: `${c.flag} ${c.country}`,
          structured: { header: `${c.flag} ${c.country}`, headline: c.headline,
                        bullets: c.bullets, quote: c.quote, tags: c.tags },
          testid: `mh-country-${c.country.toLowerCase().replace(/\s+/g, "-")}`,
        });
      });
    }
    if (folder === "print") {
      cards.push({ id: "print.flyer", title: "One-page flyer",
                   body: bodyOf("print.flyer", FLYER_TEXT, overrides), testid: "mh-flyer",
                   wide: true });
      cards.push({ id: "print.email", title: "Email template",
                   body: bodyOf("print.email", EMAIL_TEMPLATE, overrides), testid: "mh-email",
                   wide: true });
      cards.push({ id: "print.slides", title: "Presentation outline",
                   body: bodyOf("print.slides", SLIDES_OUTLINE, overrides), testid: "mh-slides",
                   wide: true });
    }
    if (folder === "audience") {
      AUDIENCE_TEMPLATES.forEach((a) => {
        const id = `audience.${a.audience.toLowerCase().replace(/[^a-z]+/g, "-")}`;
        cards.push({
          id, title: a.audience,
          structured: { header: a.audience, headline: a.headline, bullets: a.bullets, quote: a.quote },
          testid: `mh-audience-${a.audience.toLowerCase().replace(/[^a-z]+/g, "-")}`,
        });
      });
      cards.push({ id: "audience.phone", title: "Phone / in-person script",
                   body: bodyOf("audience.phone", PHONE_SCRIPT, overrides),
                   testid: "mh-phone-script", wide: true });
    }
    return cards;
  }, [folder, overrides]);

  // --- Copy all in folder ---
  const copyAllInFolder = async () => {
    if (folder === "compliance") { toast.info("Compliance folder has no shareable copies."); return; }
    const chunks = activeCards.map((c) => {
      const body = c.body || (c.structured ? renderStructuredBody(c.structured) : "");
      return composeShare({ body, contact, agentCode });
    });
    const text = chunks.join("\n\n— — —\n\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${chunks.length} templates.`);
    } catch {
      toast.error("Clipboard blocked.");
    }
  };

  // --- Download personalised PDF flyer ---
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadFlyer = async () => {
    if (!agentCode) { toast.error("Sign in with your agent code first."); return; }
    setPdfBusy(true);
    try {
      const res = await fetch(`${API}/agents/marketing-flyer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: agentCode, ...contact }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `MAVULA-Flyer-${agentCode}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Flyer downloaded.");
    } catch (e) {
      toast.error("Could not generate flyer.");
    } finally { setPdfBusy(false); }
  };

  return (
    <section className="py-10 md:py-14" data-testid="marketing-hub">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Marketing Hub</span></div>
            <h2 className="font-display text-3xl md:text-5xl mv-green-text mt-3 leading-[1.05]">
              Share MAVULA <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>the right way.</span>
            </h2>
            <p className="mt-3 max-w-2xl text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
              Approved copy for WhatsApp, print and outreach. Locked wording protects the brand — add your contact block below and copy in one click.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--mv-muted)" }}>Version</div>
            <div className="font-display text-[15px] mv-green-text" data-testid="mh-version">{MARKETING_VERSION}</div>
          </div>
        </div>

        {/* Compliance banner (idea #8) */}
        <div className="mt-6 mv-card p-4 flex items-start gap-3" style={{ background: "rgba(212,175,55,0.10)" }} data-testid="mh-compliance-banner">
          <Copyright size={16} className="mt-0.5" style={{ color: "var(--mv-gold)" }}/>
          <div className="text-[13px]" style={{ color: "var(--mv-ink)" }}>
            All numbers, rates, project availability and legal claims here are <strong>subject to project contract &amp; country law</strong>.
            Never guarantee outcomes. Always share the official disclaimer.
          </div>
        </div>

        {/* Toolbar: search + language + copy-all */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--mv-muted)" }}/>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates (e.g. Zambia, youth, flyer)…"
              className="w-full pl-9 pr-3 py-2 rounded-xl border mv-hairline text-[13.5px]"
              data-testid="mh-search-input"
            />
          </div>
          <div className="inline-flex items-center gap-2">
            <Globe2 size={15} style={{ color: "var(--mv-muted)" }}/>
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"
              data-testid="mh-lang-select"
            >
              {LANGUAGES.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
            </select>
          </div>
          <button
            type="button"
            onClick={copyAllInFolder}
            className="btn-green"
            data-testid="mh-copy-all"
            disabled={folder === "compliance"}
          >
            <Layers size={15}/> Copy all in folder
          </button>
        </div>

        {lang !== "en" && (
          <div className="mt-3 text-[12.5px] p-3 rounded-xl" style={{ background: "rgba(10,58,36,0.06)", color: "var(--mv-muted)" }} data-testid="mh-lang-notice">
            Templates for <strong>{LANGUAGES.find((l) => l.key === lang)?.label}</strong> are coming soon — English text is shown for now. Translate simply and keep the meaning clear.
          </div>
        )}

        {/* Folder tabs */}
        <div className="mt-6 flex flex-wrap gap-2 border-b mv-hairline pb-2" role="tablist">
          {FOLDERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFolder(f.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] transition ${folder === f.key ? "mv-green-bg text-white" : "hover:bg-black/[0.04]"}`}
              style={folder === f.key ? {} : { color: "var(--mv-ink)" }}
              data-testid={`mh-folder-${f.key}`}
              role="tab"
              aria-selected={folder === f.key}
            >
              {f.icon}{f.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          <ContactEditor contact={contact} setContact={setContact}/>
        </div>

        {/* Personalised PDF flyer button (Print folder only) */}
        {folder === "print" && (
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={downloadFlyer}
              disabled={pdfBusy}
              className="btn-green"
              data-testid="mh-flyer-pdf-btn"
            >
              <Download size={15}/> {pdfBusy ? "Generating…" : "Download personalised PDF flyer"}
            </button>
          </div>
        )}

        {/* Content by folder */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          {folder !== "compliance" && activeCards.map((c) => {
            const bodyStr = c.body || (c.structured ? renderStructuredBody(c.structured) : "");
            const cardMatches = match(bodyStr) || match(c.title || "");
            const commonProps = {
              id: c.id, testid: c.testid, contact, agentCode,
              matches: cardMatches, activeSearch,
            };
            if (c.structured) {
              return (
                <div key={c.id} className={c.wide ? "md:col-span-2" : ""}>
                  <StructuredCard {...commonProps} {...c.structured}/>
                </div>
              );
            }
            return (
              <div key={c.id} className={c.wide ? "md:col-span-2" : ""}>
                <CopyCard {...commonProps} title={c.title} body={c.body}/>
              </div>
            );
          })}

          {folder === "compliance" && (
            <>
              <div className="mv-card p-6" data-testid="mh-must-include">
                <div className="font-display text-[17px] mv-green-text mb-3">✅ ALWAYS include</div>
                <ul className="space-y-2 text-[14px]">
                  {MUST_INCLUDE.map((m, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check size={15} className="mt-0.5" style={{ color: "#0A7A2C" }}/>
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mv-card p-6" data-testid="mh-never-say">
                <div className="font-display text-[17px] mb-3" style={{ color: "#8a2020" }}>❌ NEVER say</div>
                <ul className="space-y-2 text-[14px]">
                  {NEVER_SAY.map((m, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <AlertTriangle size={15} className="mt-0.5" style={{ color: "#8a2020" }}/>
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mv-card p-6 md:col-span-2" data-testid="mh-how-to-use">
                <div className="font-display text-[17px] mv-green-text mb-3">How to use these templates</div>
                <ol className="list-decimal ml-5 space-y-1 text-[14px]">
                  {HOW_TO_USE.map((h, i) => <li key={i}>{h}</li>)}
                </ol>
              </div>
              <div className="mv-card p-6 md:col-span-2" style={{ background: "rgba(212,175,55,0.08)" }}>
                <div className="flex items-start gap-3">
                  <Quote size={20} className="mt-1" style={{ color: "var(--mv-gold)" }}/>
                  <div className="text-[14px]" style={{ color: "var(--mv-ink)" }}>
                    <strong>Required disclaimer (auto-appended if missing):</strong><br/>
                    <span className="font-accent italic">"{REQUIRED_DISCLAIMER}"</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function renderStructuredBody({ header, headline, bullets, quote, tags }) {
  const lines = [];
  if (header)   lines.push(header);
  if (headline) lines.push(headline);
  if (bullets && bullets.length) {
    lines.push("");
    bullets.forEach((b) => lines.push(`✅ ${b}`));
  }
  if (quote) { lines.push(""); lines.push(`"${quote}"`); }
  if (tags)  { lines.push(""); lines.push(tags); }
  lines.push("");
  lines.push(REQUIRED_DISCLAIMER);
  return lines.join("\n");
}
